import { Badge, Button, Card, Stack, Tabs, Text, useMantineTheme } from "@mantine/core";
import { createFileRoute } from "@tanstack/react-router";
import dayjs from "dayjs";
import { memo, useEffect, useState } from "react";
import { useStopwatch } from "react-timer-hook";

import { DelayDialog } from "@/components/@custom/DelayDialog";
import { Plate } from "@/components/@custom/Plate";
import { Queue, ServiceStatus } from "@/domain";
import { ExpeditionTabV2 } from "@/features/expedicao/components/ExpeditionTabV2";
import { useSession } from "@/hooks/@custom/useSession";
import { MODAL_HANDLER } from "@/lib/Mantine";
import { ExpeditionQueueStatus } from "@/pages/App/Expedition/ExpeditionHome/components/ExpeditionQueueStatus/ExpeditionQueueStatus";
import { LastUpdateExpedition } from "@/pages/App/Expedition/ExpeditionHome/components/LastUpdateExpedition";
import { QueueCompany, useGetCheckinsCompanyActivesByCompanyQuery, useGetQueueActiveByCompanyQuery, useGetQueueByCompanyQuery } from "@/store/apis/nextcargo";
import { cn } from "@/utils/cn";

export type QueueStatusService = ServiceStatus;
export type ModalToleranceType = "enable-tolerance" | "disable-tolerance" | "end-attendance";
export interface TruckToAttend {
  queue: Queue;
  checkinId?: number | null;
}

export const Route = createFileRoute("/expedicao/")({
  component: ExpeditionHome
});

function ExpeditionHome() {
  const { expedition } = useSession();
  const companyId = Number(expedition?.company.id);

  const apiOptions = {
    args: {
      id: companyId
    },
    config: {
      skip: !companyId
    }
  };

  const mapFromActiveQueues = (item: QueueCompany) => item.id;

  const { data: activeQueues } = useGetQueueActiveByCompanyQuery(apiOptions.args, apiOptions.config);
  const { data: queues } = useGetQueueByCompanyQuery(apiOptions.args, apiOptions.config);
  const { data: activeCheckins } = useGetCheckinsCompanyActivesByCompanyQuery(apiOptions.args, {
    skip: !companyId,
    selectFromResult: (state) => {
      const grouped = queues?.grouped ?? [];
      const original = queues?.original ?? [];

      const activeCheckins = state.data?.original ?? [];
      const innerActiveQueues = activeQueues?.original ?? [];

      // map queueId to isPresent from activeCheckins
      const activeCheckinMap = activeCheckins.reduce<Record<number, boolean | undefined>>((acc, curr) => {
        if (curr.queue && typeof curr.isPresent !== "undefined") {
          acc[curr.queue.id] = curr.isPresent;
        }
        return acc;
      }, {});

      const activeCheckinIds: number[] = activeCheckins.map((item) => item.queue?.id);
      const activeQueueIds: number[] = innerActiveQueues.map(mapFromActiveQueues);

      const $activeCheckins = original
        .filter((item) => activeCheckinIds.includes(item.id) && !activeQueueIds.includes(item.id))
        .map((item) => ({
          ...item,
          isCheckin: typeof activeCheckinMap[item.id] !== "undefined" ? Boolean(activeCheckinMap[item.id]) : item.isCheckin
        }));

      const $activeCheckinsGrouped = grouped
        .map((group) => {
          const filteredGroupQueues = group.queues
            .filter((item) => activeCheckinIds.includes(item.id) && !activeQueueIds.includes(item.id))
            .map((item) => ({
              ...item,
              isCheckin: typeof activeCheckinMap[item.id] !== "undefined" ? Boolean(activeCheckinMap[item.id]) : item.isCheckin
            }));

          return {
            ...group,
            queues: filteredGroupQueues,
            length: filteredGroupQueues.length
          };
        })
        .filter((group) => group.queues.length > 0);

      return {
        ...state,
        data: {
          ...state.data,
          $transformedFromQueues: {
            original: $activeCheckins,
            grouped: $activeCheckinsGrouped
          }
        }
      };
    }
  });
  const attendanceTotal = activeQueues?.original.length || 0;
  const checkinTotal = activeCheckins.$transformedFromQueues.original.length;

  const [tab, setTab] = useState<string | null>(attendanceTotal > 0 ? "attendance" : "queue");

  const { shippingCompanyId } = Route.useRouteContext();
  const { colors } = useMantineTheme();

  //! ############################################################

  const queuesTotal = queues?.original?.length || 0;
  const badgeStyles = cn(["absolute -top-2 -right-2 z-10"]);
  const getColor = () => {
    switch (tab) {
      case "attendance":
        return colors.green[6];
      case "courtyard":
        return colors.blue[6];
      case "queue":
        return colors.yellow[6];
      default:
        return colors.gray[6];
    }
  };

  useEffect(() => {
    if (activeQueues?.original.length === 0) {
      setTab("queue");
    }
  }, [activeQueues]);

  return (
    <>
      <div className="w-full">
        {!shippingCompanyId && (
          <div className="flex flex-1 flex-col gap-16">
            <div className="flex flex-col gap-6">
              <LastUpdateExpedition />
              <ExpeditionQueueStatus />
            </div>

            <Tabs
              variant="outline"
              value={tab}
              onChange={setTab}
              keepMounted={false}
              style={{ "--tab-border-color": getColor(), "--tabs-list-border-width": "2px" }}
              styles={{
                tab: { borderWidth: 2 }
              }}
            >
              <Tabs.List justify="center" w={400} className="mx-auto" classNames={{ list: "!gap-2 md:!gap-4" }} id="top" grow>
                {attendanceTotal > 0 && (
                  <Tabs.Tab value="attendance" color="green" className="relative">
                    <p className={cn({ "font-bold text-green-500": tab === "attendance" }, "font-medium")}>Atendimento</p>
                    <>
                      {/* <Tooltip target="#attendance-badge" label={`${attendanceTotal} veículos em atendimento`} position="top" color="green" withArrow /> */}
                      <Badge className={badgeStyles} size="md" color="green" c="white" id="attendance-badge" circle={attendanceTotal.toString().length === 1}>
                        {attendanceTotal}
                      </Badge>
                    </>
                  </Tabs.Tab>
                )}

                <Tabs.Tab value="courtyard" color="blue" className="relative">
                  <p className={cn({ "font-bold text-blue-500": tab === "courtyard" }, "font-medium")}>Pátio</p>
                  <>
                    {/* <Tooltip target="#courtyard-badge" label={`${checkinTotal} veículos no pátio`} position="top" color="blue" withArrow /> */}
                    <Badge className={badgeStyles} size="md" color="blue" c="white" id="courtyard-badge" circle={checkinTotal.toString().length === 1}>
                      {checkinTotal}
                    </Badge>
                  </>
                </Tabs.Tab>

                <Tabs.Tab value="queue" color="yellow" className="relative">
                  <p className={cn({ "font-bold text-yellow-500": tab === "queue" }, "font-medium")}>Fila</p>
                  <>
                    {/* <Tooltip target="#queue-badge" label={`${queuesTotal} veículos na fila`} position="top" color="yellow" withArrow /> */}
                    <Badge className={badgeStyles} size="md" color="yellow" c="white" id="queue-badge" circle={queuesTotal?.toString().length === 1}>
                      {queuesTotal - attendanceTotal}
                    </Badge>
                  </>
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="attendance">
                <Stack>
                  {activeQueues?.original.map((item) => {
                    const handleAction = (actionType: "backToQueue" | "shutdownAttendance") => () => {
                      const matcher = new Map<typeof actionType, () => void>([
                        [
                          "backToQueue",
                          () => {
                            MODAL_HANDLER.expeditions.actions.attendance.backToQueue(item);
                          }
                        ],
                        [
                          "shutdownAttendance",
                          () => {
                            MODAL_HANDLER.expeditions.actions.attendance.shutdown(item);
                          }
                        ]
                      ]);

                      const handler = matcher.get(actionType);
                      if (!handler) return;
                      handler();
                    };

                    return (
                      <Card className="grid! auto-rows-min grid-cols-1 place-content-center place-items-center gap-3 md:grid-cols-3" key={item.id} shadow="md" withBorder>
                        {item?.scheduling?.truck.licensePlate && <Plate plate={item?.scheduling?.truck.licensePlate} color="text-red-500" />}

                        <CardTimer value={item.updatedAt!} />

                        <div className="grid grid-cols-2 gap-1 md:grid-cols-1">
                          <Button color="red" onClick={handleAction("backToQueue")} size="compact-sm">
                            Voltar para Fila
                          </Button>

                          <Button color="green" onClick={handleAction("shutdownAttendance")} size="compact-sm">
                            Encerrar Atendimento
                          </Button>
                        </div>
                      </Card>
                    );
                  })}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="courtyard">
                <ExpeditionTabV2 queues={activeCheckins.$transformedFromQueues} actions={["shipping", "call"]}>
                  <ExpeditionTabV2.Filter />
                  <ExpeditionTabV2.Content row={{ render: (item) => <ExpeditionTabV2.Row item={item} /> }} />
                </ExpeditionTabV2>
              </Tabs.Panel>

              <Tabs.Panel value="queue" classNames={{ panel: cn({ "relative!": true, "h-[300dvh]": false }) }}>
                <ExpeditionTabV2 queues={queues} actions="shipping" filterFn={activeQueues?.original.map((item) => item.id)}>
                  <ExpeditionTabV2.Filter />
                  <ExpeditionTabV2.Content row={{ render: (item) => <ExpeditionTabV2.Row item={item} /> }} />

                  <ExpeditionTabV2.Sidebar />
                </ExpeditionTabV2>
              </Tabs.Panel>
            </Tabs>
          </div>
        )}
      </div>

      <DelayDialog />
    </>
  );
}

const CardTimer = memo(({ value }: { value?: string }) => {
  useStopwatch({
    autoStart: true
  });

  if (!value) return null;

  const lastUpdate = dayjs().diff(dayjs(value));
  const formattedLastUpdate = dayjs.utc(lastUpdate).format("HH[h] mm[m] ss[s]");
  return (
    <Card bg="gray.1" radius="sm" p="xs" withBorder>
      <Text fw="bold" size="lg">
        {formattedLastUpdate}
      </Text>
    </Card>
  );
});
CardTimer.displayName = "CardTimer";
